import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  bpsToPercent,
  createSuperAdminCoupon,
  percentToBps,
  SaCoupon,
  updateSuperAdminCoupon,
} from "../../../helpers/super_admin_coupons_helper";
import { getSuperAdminPlans, SaPlan } from "../../../helpers/super_admin_plans_helper";

type FormValues = {
  code: string;
  discountPercent: string;
  duration_type: "first_invoice" | "limited_cycles" | "lifetime";
  duration_cycles: string;
  max_redemptions: string;
  valid_from: string;
  valid_until: string;
  applicable_plan_ids: number[];
  applicable_currencies: string[];
  is_active: boolean;
};

const emptyValues = (): FormValues => ({
  code: "",
  discountPercent: "10",
  duration_type: "first_invoice",
  duration_cycles: "",
  max_redemptions: "",
  valid_from: "",
  valid_until: "",
  applicable_plan_ids: [],
  applicable_currencies: ["BRL"],
  is_active: true,
});

/** ISO UTC → yyyy-mm-dd para input date. */
const isoToDateInput = (iso: string | null | undefined): string => {
  if (!iso) return "";
  return iso.slice(0, 10);
};

/** yyyy-mm-dd → ISO início do dia UTC (API datetime). */
const dateInputToIso = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return `${trimmed}T00:00:00Z`;
};

const couponToForm = (coupon: SaCoupon): FormValues => ({
  code: coupon.code || "",
  discountPercent: String(bpsToPercent(coupon.discount_percent_bps)),
  duration_type:
    coupon.duration_type === "limited_cycles" ||
    coupon.duration_type === "lifetime"
      ? coupon.duration_type
      : "first_invoice",
  duration_cycles:
    coupon.duration_cycles != null ? String(coupon.duration_cycles) : "",
  max_redemptions:
    coupon.max_redemptions != null ? String(coupon.max_redemptions) : "",
  valid_from: isoToDateInput(coupon.valid_from),
  valid_until: isoToDateInput(coupon.valid_until),
  applicable_plan_ids: coupon.applicable_plan_ids ?? [],
  applicable_currencies:
    coupon.applicable_currencies && coupon.applicable_currencies.length > 0
      ? coupon.applicable_currencies
      : ["BRL"],
  is_active: !!coupon.is_active,
});

type Props = {
  isOpen: boolean;
  coupon: SaCoupon | null;
  onClose: () => void;
  onSaved: () => void;
};

/**
 * Create/edit cupom SA — % na UI, bps na API (DOC 02 § 4 · 05 tipagem).
 */
const CouponFormModal = ({ isOpen, coupon, onClose, onSaved }: Props) => {
  const { t, i18n } = useTranslation();
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [initialValues, setInitialValues] = useState<FormValues>(emptyValues());
  const [plans, setPlans] = useState<SaPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [deactivateConfirmed, setDeactivateConfirmed] = useState(false);
  const [wasActiveOnOpen, setWasActiveOnOpen] = useState(true);
  const editingId = coupon?.id ?? null;
  const redemptionCount = coupon?.redemption_count ?? 0;

  useEffect(() => {
    if (!isOpen) {
      setFormError(null);
      setSaving(false);
      setInitialValues(emptyValues());
      setDeactivateConfirmed(false);
      setWasActiveOnOpen(true);
      return;
    }
    setFormError(null);
    setSaving(false);
    setDeactivateConfirmed(false);
    const nextValues = coupon ? couponToForm(coupon) : emptyValues();
    setInitialValues(nextValues);
    setWasActiveOnOpen(coupon ? !!coupon.is_active : true);
  }, [isOpen, coupon]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setPlansLoading(true);
    getSuperAdminPlans({ per_page: 100, is_active: true })
      .then((res) => {
        if (cancelled) return;
        setPlans(res?.data || []);
      })
      .catch(() => {
        if (!cancelled) setPlans([]);
      })
      .finally(() => {
        if (!cancelled) setPlansLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        code: Yup.string()
          .trim()
          .min(2, t("sa.coupons.form.validation.codeMin"))
          .max(64, t("sa.coupons.form.validation.codeMax"))
          .matches(
            /^[A-Z0-9_-]+$/,
            t("sa.coupons.form.validation.codeFormat")
          )
          .required(t("sa.coupons.form.validation.codeRequired")),
        discountPercent: Yup.number()
          .typeError(t("sa.coupons.form.validation.percentInvalid"))
          .min(0.01, t("sa.coupons.form.validation.percentRange"))
          .max(100, t("sa.coupons.form.validation.percentRange"))
          .required(t("sa.coupons.form.validation.percentRequired")),
        duration_type: Yup.string()
          .oneOf(["first_invoice", "limited_cycles", "lifetime"])
          .required(),
        duration_cycles: Yup.number()
          .transform((value, original) =>
            original === "" || original === null || original === undefined
              ? undefined
              : value
          )
          .when("duration_type", {
            is: "limited_cycles",
            then: (schema) =>
              schema
                .typeError(t("sa.coupons.form.validation.cyclesInvalid"))
                .integer(t("sa.coupons.form.validation.cyclesInvalid"))
                .min(1, t("sa.coupons.form.validation.cyclesInvalid"))
                .max(120, t("sa.coupons.form.validation.cyclesInvalid"))
                .required(t("sa.coupons.form.validation.cyclesRequired")),
            otherwise: (schema) => schema.nullable().notRequired(),
          }),
        max_redemptions: Yup.number()
          .transform((value, original) =>
            original === "" || original === null || original === undefined
              ? undefined
              : value
          )
          .nullable()
          .min(1, t("sa.coupons.form.validation.maxRedemptionsInvalid"))
          .integer(t("sa.coupons.form.validation.maxRedemptionsInvalid")),
        valid_from: Yup.string().nullable(),
        valid_until: Yup.string()
          .nullable()
          .test(
            "after-from",
            t("sa.coupons.form.validation.validUntilAfter"),
            function (until) {
              const from = this.parent.valid_from as string;
              if (!from || !until) return true;
              return until >= from;
            }
          ),
        is_active: Yup.boolean(),
      }),
    [t, i18n.language]
  );

  const validation = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setFormError(null);

      const mustConfirmDeactivate =
        editingId !== null &&
        redemptionCount > 0 &&
        wasActiveOnOpen &&
        !values.is_active;

      if (mustConfirmDeactivate && !deactivateConfirmed) {
        setSubmitting(false);
        return;
      }

      setSaving(true);

      const percent = Number(values.discountPercent);
      const bps = percentToBps(percent);
      const planIds =
        values.applicable_plan_ids.length > 0
          ? values.applicable_plan_ids
          : null;
      const payload = {
        code: values.code.trim().toUpperCase(),
        discount_percent_bps: bps,
        duration_type: values.duration_type,
        duration_cycles:
          values.duration_type === "limited_cycles"
            ? Number(values.duration_cycles)
            : null,
        max_redemptions: values.max_redemptions
          ? Number(values.max_redemptions)
          : null,
        valid_from: dateInputToIso(values.valid_from),
        valid_until: dateInputToIso(values.valid_until),
        applicable_plan_ids: planIds,
        // BRL e/ou USD — cupom BRL não vale em checkout USD se só BRL marcado.
        applicable_currencies:
          values.applicable_currencies.length > 0
            ? values.applicable_currencies
            : ["BRL"],
        is_active: values.is_active,
      };

      try {
        if (editingId) {
          const res = await updateSuperAdminCoupon(editingId, payload);
          toast.success(res?.message || t("sa.coupons.toast.updated"), {
            autoClose: 2500,
          });
        } else {
          const res = await createSuperAdminCoupon(payload);
          toast.success(res?.message || t("sa.coupons.toast.created"), {
            autoClose: 2500,
          });
        }
        onSaved();
        onClose();
      } catch (err: any) {
        const details =
          err?.details?.code?.[0] ||
          err?.details?.discount_percent_bps?.[0] ||
          err?.details?.duration_cycles?.[0] ||
          err?.details?.valid_until?.[0] ||
          err?.message;
        setFormError(details || t("sa.coupons.error.save"));
      } finally {
        setSaving(false);
      }
    },
  });

  const title = editingId
    ? t("sa.coupons.form.titleEdit")
    : t("sa.coupons.form.titleNew");

  const showCycles = validation.values.duration_type === "limited_cycles";

  const awaitingDeactivateConfirm =
    editingId !== null &&
    redemptionCount > 0 &&
    wasActiveOnOpen &&
    !validation.values.is_active &&
    !deactivateConfirmed;

  const discountPreviewBps = (() => {
    const n = Number(validation.values.discountPercent);
    if (!Number.isFinite(n) || n <= 0) return null;
    return percentToBps(n);
  })();

  const togglePlan = (planId: number) => {
    const current = validation.values.applicable_plan_ids;
    const next = current.includes(planId)
      ? current.filter((id) => id !== planId)
      : [...current, planId];
    validation.setFieldValue("applicable_plan_ids", next);
  };

  const fieldInvalid = (name: keyof FormValues) =>
    Boolean(validation.touched[name] && validation.errors[name]);

  const fieldError = (name: keyof FormValues) =>
    fieldInvalid(name) ? String(validation.errors[name]) : undefined;

  return (
    <Modal
      isOpen={isOpen}
      toggle={() => !saving && onClose()}
      centered
      size="lg"
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
        }}
      >
        <ModalHeader toggle={() => !saving && onClose()}>{title}</ModalHeader>
        <ModalBody>
          {formError ? (
            <Alert
              color="danger"
              className="py-2"
              toggle={() => setFormError(null)}
            >
              {formError}
            </Alert>
          ) : null}

          {awaitingDeactivateConfirm ? (
            <Alert color="warning" className="d-flex flex-column gap-2">
              <span>
                {t("sa.coupons.deactivate.confirm", {
                  count: redemptionCount,
                })}
              </span>
              <div className="d-flex flex-wrap gap-2">
                <Button
                  type="button"
                  color="warning"
                  size="sm"
                  disabled={saving}
                  onClick={() => {
                    setDeactivateConfirmed(true);
                    validation.handleSubmit();
                  }}
                >
                  {t("sa.coupons.deactivate.confirmButton")}
                </Button>
                <Button
                  type="button"
                  color="light"
                  size="sm"
                  disabled={saving}
                  onClick={() => {
                    validation.setFieldValue("is_active", true);
                    setDeactivateConfirmed(false);
                  }}
                >
                  {t("sa.coupons.form.cancel")}
                </Button>
              </div>
            </Alert>
          ) : null}

          <Row className="g-3">
            <Col md={6}>
              <Label for="sa-coupon-code">{t("sa.coupons.form.code")}</Label>
              <Input
                id="sa-coupon-code"
                name="code"
                value={validation.values.code}
                onChange={(e) => {
                  const next = e.target.value
                    .toUpperCase()
                    .replace(/\s+/g, "");
                  validation.setFieldValue("code", next);
                }}
                onBlur={validation.handleBlur}
                disabled={saving}
                invalid={fieldInvalid("code")}
                autoComplete="off"
              />
              {fieldError("code") ? (
                <FormFeedback type="invalid">{fieldError("code")}</FormFeedback>
              ) : (
                <div className="form-text">{t("sa.coupons.form.codeHelp")}</div>
              )}
            </Col>
            <Col md={6}>
              <Label for="sa-coupon-percent">
                {t("sa.coupons.form.discountPercent")}
              </Label>
              <Input
                id="sa-coupon-percent"
                name="discountPercent"
                type="number"
                min={0.01}
                max={100}
                step="0.01"
                value={validation.values.discountPercent}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                disabled={saving}
                invalid={fieldInvalid("discountPercent")}
              />
              {fieldError("discountPercent") ? (
                <FormFeedback type="invalid">
                  {fieldError("discountPercent")}
                </FormFeedback>
              ) : (
                <div className="form-text">
                  {discountPreviewBps != null
                    ? t("sa.coupons.form.discountBpsPreview", {
                        percent: validation.values.discountPercent,
                        bps: discountPreviewBps,
                      })
                    : t("sa.coupons.form.discountPercentHelp")}
                </div>
              )}
            </Col>
            <Col md={showCycles ? 6 : 12}>
              <Label for="sa-coupon-duration">
                {t("sa.coupons.form.durationType")}
              </Label>
              <Input
                id="sa-coupon-duration"
                name="duration_type"
                type="select"
                value={validation.values.duration_type}
                onChange={(e) => {
                  validation.handleChange(e);
                  if (e.target.value !== "limited_cycles") {
                    validation.setFieldValue("duration_cycles", "");
                  }
                }}
                onBlur={validation.handleBlur}
                disabled={saving}
              >
                <option value="first_invoice">
                  {t("sa.coupons.duration.first_invoice")}
                </option>
                <option value="limited_cycles">
                  {t("sa.coupons.duration.limited_cycles")}
                </option>
                <option value="lifetime">
                  {t("sa.coupons.duration.lifetime")}
                </option>
              </Input>
            </Col>
            {showCycles ? (
              <Col md={6}>
                <Label for="sa-coupon-cycles">
                  {t("sa.coupons.form.durationCycles")}
                </Label>
                <Input
                  id="sa-coupon-cycles"
                  name="duration_cycles"
                  type="number"
                  min={1}
                  max={120}
                  value={validation.values.duration_cycles}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={saving}
                  invalid={fieldInvalid("duration_cycles")}
                />
                {fieldError("duration_cycles") ? (
                  <FormFeedback type="invalid">
                    {fieldError("duration_cycles")}
                  </FormFeedback>
                ) : (
                  <div className="form-text">
                    {t("sa.coupons.form.durationCyclesHelp")}
                  </div>
                )}
              </Col>
            ) : null}
            <Col md={4}>
              <Label for="sa-coupon-max">
                {t("sa.coupons.form.maxRedemptions")}
              </Label>
              <Input
                id="sa-coupon-max"
                name="max_redemptions"
                type="number"
                min={1}
                value={validation.values.max_redemptions}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                disabled={saving}
                invalid={fieldInvalid("max_redemptions")}
                placeholder={t("sa.coupons.form.maxRedemptionsPlaceholder")}
              />
              {fieldError("max_redemptions") ? (
                <FormFeedback type="invalid">
                  {fieldError("max_redemptions")}
                </FormFeedback>
              ) : (
                <div className="form-text">
                  {t("sa.coupons.form.maxRedemptionsHelp")}
                </div>
              )}
            </Col>
            <Col md={4}>
              <Label for="sa-coupon-from">{t("sa.coupons.form.validFrom")}</Label>
              <Input
                id="sa-coupon-from"
                name="valid_from"
                type="date"
                value={validation.values.valid_from}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                disabled={saving}
              />
            </Col>
            <Col md={4}>
              <Label for="sa-coupon-until">
                {t("sa.coupons.form.validUntil")}
              </Label>
              <Input
                id="sa-coupon-until"
                name="valid_until"
                type="date"
                value={validation.values.valid_until}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                disabled={saving}
                invalid={fieldInvalid("valid_until")}
              />
              {fieldError("valid_until") ? (
                <FormFeedback type="invalid">
                  {fieldError("valid_until")}
                </FormFeedback>
              ) : null}
            </Col>
            <Col xs={12}>
              <Label>{t("sa.coupons.form.applicablePlans")}</Label>
              {plansLoading ? (
                <div className="text-muted small">
                  <Spinner size="sm" className="me-1" />
                  {t("sa.coupons.form.plansLoading")}
                </div>
              ) : plans.length === 0 ? (
                <div className="text-muted small">
                  {t("sa.coupons.form.plansEmpty")}
                </div>
              ) : (
                <div className="border rounded p-2" style={{ maxHeight: 160, overflowY: "auto" }}>
                  {plans.map((plan) => (
                    <div key={plan.id} className="form-check">
                      <Input
                        id={`sa-coupon-plan-${plan.id}`}
                        type="checkbox"
                        className="form-check-input"
                        checked={validation.values.applicable_plan_ids.includes(
                          plan.id
                        )}
                        onChange={() => togglePlan(plan.id)}
                        disabled={saving}
                      />
                      <Label
                        className="form-check-label"
                        for={`sa-coupon-plan-${plan.id}`}
                      >
                        {plan.name}
                      </Label>
                    </div>
                  ))}
                </div>
              )}
              <div className="form-text">
                {t("sa.coupons.form.applicablePlansHelp")}
              </div>
            </Col>
            <Col xs={12}>
              <Label className="form-label">
                {t("sa.coupons.form.currencies")}
              </Label>
              <div className="d-flex flex-wrap gap-3">
                {["BRL", "USD"].map((cur) => (
                  <div className="form-check" key={cur}>
                    <Input
                      id={`sa-coupon-cur-${cur}`}
                      type="checkbox"
                      checked={validation.values.applicable_currencies.includes(
                        cur
                      )}
                      disabled={saving}
                      onChange={() => {
                        const current = validation.values.applicable_currencies;
                        const next = current.includes(cur)
                          ? current.filter((c) => c !== cur)
                          : [...current, cur];
                        validation.setFieldValue(
                          "applicable_currencies",
                          next.length ? next : ["BRL"]
                        );
                      }}
                    />
                    <Label
                      className="form-check-label"
                      for={`sa-coupon-cur-${cur}`}
                    >
                      {cur}
                    </Label>
                  </div>
                ))}
              </div>
              <div className="form-text">
                {t("sa.coupons.form.currencyHint")}
              </div>
            </Col>
            <Col xs={12}>
              <div className="form-check form-switch mt-1">
                <Input
                  id="sa-coupon-active"
                  type="switch"
                  name="is_active"
                  checked={validation.values.is_active}
                  onChange={(e) => {
                    validation.setFieldValue("is_active", e.target.checked);
                    setDeactivateConfirmed(false);
                  }}
                  disabled={saving}
                />
                <Label className="form-check-label" for="sa-coupon-active">
                  {t("sa.coupons.form.active")}
                </Label>
              </div>
              <div className="form-text">{t("sa.coupons.form.activeHelp")}</div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            color="light"
            disabled={saving}
            onClick={onClose}
          >
            {t("sa.coupons.form.cancel")}
          </Button>
          <Button type="submit" color="primary" disabled={saving || awaitingDeactivateConfirm}>
            {saving ? (
              <>
                <Spinner size="sm" className="me-1" />
                {t("sa.coupons.form.saving")}
              </>
            ) : (
              t("sa.coupons.form.save")
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CouponFormModal;
